import { Form, Select } from "antd";
import { useCallback, useEffect, useState } from "react";
import { DefaultCriteriaEditorProps } from "../../../../types/bulkEmails/CriteriaOption";
import { useFetchAccountNumber } from "../../hooks/useFetchAccountNumber";
import { useAccountNumberRules } from "../../hooks/useAccountNumberRules";
import { useTagRender } from "../../hooks/useAccountNumberTagRender";
import _ from "lodash";

export type AccountNumberOption = {
  id: number | null;
  name: string;
  isPending?: boolean;
};

export const AccountNumberCriteriaSelect = ({ value, onChange, form, formKey }: DefaultCriteriaEditorProps) => {
  const { isLoading, findByNumber } = useFetchAccountNumber();

  const [options, setOptions] = useState<AccountNumberOption[]>([]);

  const { rules } = useAccountNumberRules(options);

  const { tagRender } = useTagRender(options);

  const normalizeValue = (value: string) => (_.isNaN(+value) ? value : (+value).toString());

  const handleChange = useCallback(
    (selectedValues: string[]) => {
      const normalized = selectedValues.map((val) => normalizeValue(val));
      onChange(normalized);
      setOptions((prevOptions) => prevOptions.filter((option) => normalized.includes(option.name)));
    },
    [onChange]
  );

  const upsertOption = useCallback((options: AccountNumberOption[], option: AccountNumberOption) => {
    const nextOptions = options.filter((item) => item.name !== option.name);
    return [...nextOptions, option];
  }, []);

  useEffect(() => {
    options.length && form.validateFields([formKey]).catch(() => undefined);
  }, [form, formKey, options]);

  const onSelect = useCallback(
    async (value: string) => {
      const normalizedValue = normalizeValue(value);

      if (_.isNaN(+normalizedValue)) {
        setOptions((prevOptions) => upsertOption(prevOptions, { id: null, name: value, isPending: false }));
        return;
      } else {
        setOptions((prevOptions) =>
          upsertOption(prevOptions, { id: null, name: normalizedValue.toString(), isPending: true })
        );
      }

      const account = await findByNumber(+normalizedValue);

      if (account) {
        setOptions((prevOptions) => {
          return upsertOption(prevOptions, { id: account.id, name: account.name, isPending: false });
        });
      } else {
        setOptions((prevOptions) => upsertOption(prevOptions, { id: null, name: value, isPending: false }));
      }
    },
    [findByNumber, upsertOption]
  );

  return (
    <Form.Item
      name={formKey}
      className="w-100 mb-0"
      rules={rules}
      preserve={false}
      normalize={(values: string[]) => values.map((value) => normalizeValue(value))}
    >
      <Select
        className="w-100"
        mode="tags"
        value={value}
        onChange={handleChange}
        onSelect={onSelect}
        tagRender={tagRender}
        disabled={isLoading}
        loading={isLoading}
      >
        {options.map((option) => (
          <Select.Option key={option.name} value={option.name}>
            {option.name}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  );
};
